import { produce } from 'immer';

import { mainActionTypes } from './main.types';

const initialState = {
    diagrams: [],
    templates: [],

    // diagram details (title, description, db_engine)
    diagramDetailsCanvas: null,
    diagramDbEngineCanvas: null,

    // fetch diagram errors
    isDiagramCanvasDeleted: false,
    isDiagramCanvasNotFound: false,

    feedbackSentStatus: null,
    inviteSentStatus: null,

    // sidebar
    isSidebarCollapsed: false,
    isSidebarProfileMenuEnabled: false,
    isSidebarWorkspaceMenuEnabled: false,

    // diagram modals
    isModalDiagramCreateEnabled: false,
    modalDiagramCreateData: { page: null },

    isModalDiagramCreateNewEnabled: false,
    modalDiagramCreateNewData: { page: null },

    isModalDiagramImportSqlEnabled: false,
    modalDiagramImportSqlData: { id: null, template: null },

    isModalDiagramRenameEnabled: false,
    modalDiagramRenameData: { id: null, diagram: null },

    isModalDiagramCloneEnabled: false,
    modalDiagramCloneData: { id: null, diagram: null },

    isModalDiagramSoftDeleteEnabled: false,
    modalDiagramSoftDeleteData: { id: null, diagram: null },

    isModalDiagramDeleteEnabled: false,
    modalDiagramDeleteData: { id: null, diagram: null },

    isModalDiagramRestoreEnabled: false,
    modalDiagramRestoreData: { id: null, diagram: null },

    isModalDiagramShareEnabled: false,
    modalDiagramShareData: { id: null, diagram: null },

    isModalDiagramDetailsEnabled: false,
    modalDiagramDetailsData: { id: null, diagram: null },

    // template modals
    isModalTemplateCloneEnabled: false,
    modalTemplateCloneData: { id: null, template: null },

    isModalTemplateDetailsEnabled: false,
    modalTemplateDetailsData: { id: null, template: null },

    // support / feedback modals
    isModalSupportEnabled: false,
    modalSupportData: { type: null },

    // feedback thanks modals
    isModalFeedbackThanksEnabled: false,
    modalFeedbackThanksData: { type: null }, // { type: 'feedback' },

    // pricing plan modals
    isModalPricingPlanUpgradeEnabled: false,
    modalPricingPlanUpgradeData: { page: null },

    isModalPricingPlanLimitEnabled: false,
    modalPricingPlanLimitType: null,

    // share / invite modals
    isModalInvitePeopleEnabled: false,

    // modal languages
    isModalLanguagesEnabled: false,

    // workspace
    isModalWorkspaceCreateEnabled: false,
};

function unfocusEnabledControls(state, draft, exceptControl = null) {
    if (state.isSidebarProfileMenuEnabled) {
        draft.isSidebarProfileMenuEnabled = false;
    }
    if (state.isModalDiagramCreateEnabled) {
        draft.isModalDiagramCreateEnabled = false;
    }
    if (state.isModalDiagramCreateNewEnabled) {
        draft.isModalDiagramCreateNewEnabled = false;
    }
    if (state.isModalDiagramImportSqlEnabled) {
        draft.isModalDiagramImportSqlEnabled = false;
        draft.modalDiagramImportSqlData = { id: null, template: null };
    }
    if (state.isModalDiagramRenameEnabled) {
        draft.isModalDiagramRenameEnabled = false;
        draft.modalDiagramRenameData = { id: null, diagram: null };
    }
    if (state.isModalDiagramCloneEnabled) {
        draft.isModalDiagramCloneEnabled = false;
        draft.modalDiagramCloneData = { id: null, diagram: null };
    }
    if (state.isModalDiagramSoftDeleteEnabled) {
        draft.isModalDiagramSoftDeleteEnabled = false;
        draft.modalDiagramSoftDeleteData = { id: null, diagram: null };
    }
    if (state.isModalDiagramDeleteEnabled) {
        draft.isModalDiagramDeleteEnabled = false;
        draft.modalDiagramDeleteData = { id: null, diagram: null };
    }
    if (state.isModalDiagramRestoreEnabled) {
        draft.isModalDiagramRestoreEnabled = false;
        draft.modalDiagramRestoreData = { id: null, diagram: null };
    }
    if (state.isModalDiagramShareEnabled) {
        draft.isModalDiagramShareEnabled = false;
        draft.modalDiagramShareData = { id: null, diagram: null };
    }
    if (state.isModalDiagramDetailsEnabled) {
        draft.isModalDiagramDetailsEnabled = false;
        draft.modalDiagramDetailsData = { id: null, diagram: null };
    }
    if (state.isModalTemplateCloneEnabled) {
        draft.isModalTemplateCloneEnabled = false;
        draft.modalTemplateCloneData = { id: null, template: null };
    }
    if (state.isModalTemplateDetailsEnabled) {
        draft.isModalTemplateDetailsEnabled = false;
        draft.modalTemplateDetailsData = { id: null, template: null };
    }
    if (state.isModalSupportEnabled) {
        draft.isModalSupportEnabled = false;
        draft.modalSupportData = { type: null };
    }
    if (state.isModalPricingPlanUpgradeEnabled) {
        draft.isModalPricingPlanUpgradeEnabled = false;
    }
    if (state.isModalPricingPlanLimitEnabled) {
        draft.isModalPricingPlanLimitEnabled = false;
    }

    if (state.isModalInvitePeopleEnabled) {
        draft.isModalInvitePeopleEnabled = false;
    }

    return draft;
}

const mainReducer = (state = initialState, action) => {
    if (state.isStoredState) {
        state = { ...initialState, ...state, isStoredState: false };
    }

    return produce(state, (draft) => {
        switch (action.type) {

            // FETCH DIAGRAMS LIST
            case mainActionTypes.MAIN_FETCH_DIAGRAMS_LIST_REQUEST: {
                draft.loading = true;
                break;
            }
            case mainActionTypes.MAIN_FETCH_DIAGRAMS_LIST_SUCCESS: {
                draft.loading = false;
                // draft.diagrams = (action.payload.data || []).sort((a, b) => new Date(b.updated_at.replace(' ', 'T')) - new Date(a.updated_at.replace(' ', 'T')));
                draft.diagrams = action.payload.data;

                if (draft.diagramDetailsCanvas?.id) {
                    let newDiagram = draft.diagrams.find(({ id }) => id === draft.diagramDetailsCanvas?.id);
                    if (newDiagram) {
                        draft.diagramDetailsCanvas = newDiagram;
                        draft.diagramDbEngineCanvas = newDiagram?.db_engine;
                    }
                }
                draft.error = null;
                break;
            }
            case mainActionTypes.MAIN_FETCH_DIAGRAMS_LIST_FAILURE: {
                draft.loading = false;
                draft.error = action.payload;
                break;
            }

            // FETCH TEMPLATES LIST
            case mainActionTypes.MAIN_FETCH_TEMPLATES_LIST_REQUEST: {
                draft.loading = true;
                break;
            }
            case mainActionTypes.MAIN_FETCH_TEMPLATES_LIST_SUCCESS: {
                draft.loading = false;
                draft.templates = action.payload.data;
                draft.error = null;
                break;
            }
            case mainActionTypes.MAIN_FETCH_TEMPLATES_LIST_FAILURE: {
                draft.loading = false;
                draft.error = action.payload;
                break;
            }

            // SIDEBAR COLLAPSE
            case mainActionTypes.TOGGLE_SIDEBAR_COLLAPSE: {
                draft.isSidebarCollapsed = !state.isSidebarCollapsed;
                break;
            }
            // SIDEBAR PROFILE MENU
            case mainActionTypes.TOGGLE_SIDEBAR_PROFILE_MENU: {
                draft.isSidebarProfileMenuEnabled = !state.isSidebarProfileMenuEnabled;
                break;
            }

            // RENAME DIAGRAM
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_RENAME: {
                const diagramId = action.payload.id || null;
                let diagram = null;

                if (diagramId) {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;
                }

                draft.isModalDiagramRenameEnabled = !state.isModalDiagramRenameEnabled;
                draft.modalDiagramRenameData = { id: diagramId, diagram };
                break;
            }
            // case mainActionTypes.MAIN_DIAGRAM_RENAME_SUCCESS: {
            //     let diagram = draft.diagrams.find(diagram => (diagram.id === action.payload.id));

            //     if (diagram) {
            //         diagram.title = action.payload.name;
            //     }
            //     break;
            // }

            // CLONE DIAGRAM
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_CLONE: {
                const page = action.payload.page; // 'main', 'canvas'
                const diagramId = action.payload.id || null;
                let diagram = null;

                draft.isModalDiagramCloneEnabled = !state.isModalDiagramCloneEnabled;
                draft.modalDiagramCloneData = { id: null, diagram: null, page };

                if (!diagramId) {
                    break;
                }

                // OPEN MODAL IN MAIN PAGE
                if (page === 'main') {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;

                    // OPEN MODAL IN CANVAS PAGE
                } else if (page === 'canvas') {
                    if (state.diagramDetailsCanvas?.id !== diagramId)
                        break;
                    diagram = state.diagramDetailsCanvas;
                }

                draft.modalDiagramCloneData = { id: diagramId, diagram, page };
                break;
            }

            // SOFT DELETE DIAGRAM (ARCHIVE)
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_SOFT_DELETE: {
                const page = action.payload.page; // 'main', 'canvas'
                const diagramId = action.payload.id || null;
                let diagram = null;

                draft.isModalDiagramSoftDeleteEnabled = !state.isModalDiagramSoftDeleteEnabled;
                draft.modalDiagramSoftDeleteData = { id: null, diagram: null, page };

                if (!diagramId) {
                    break;
                }

                // OPEN MODAL IN MAIN PAGE
                if (page === 'main') {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;

                    // OPEN MODAL IN CANVAS PAGE
                } else if (page === 'canvas') {
                    if (state.diagramDetailsCanvas?.id !== diagramId)
                        break;
                    diagram = state.diagramDetailsCanvas;
                }

                draft.modalDiagramSoftDeleteData = { id: diagramId, diagram, page };
                break;
            }
            case mainActionTypes.MAIN_DIAGRAM_SOFT_DELETE_SUCCESS: {
                // action.payload.id
                draft.diagrams = draft.diagrams.filter(diagram => (diagram.id !== action.payload.id));
                if (draft.diagramDetailsCanvas?.id === action.payload.id) {
                    draft.diagramDetailsCanvas = null;
                    draft.diagramDbEngineCanvas = null;
                }

                draft.isDiagramCanvasDeleted = true;
                break;
            }

            case mainActionTypes.MAIN_CLEAR_DIAGRAM_ERRORS: {
                draft.isDiagramCanvasDeleted = false;
                draft.isDiagramCanvasNotFound = false;
                break;
            }

            // DELETE DIAGRAM
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_DELETE: {
                const diagramId = action.payload.id || null;
                let diagram = null;

                if (diagramId) {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;
                }

                draft.isModalDiagramDeleteEnabled = !state.isModalDiagramDeleteEnabled;
                draft.modalDiagramDeleteData = { id: diagramId, diagram };
                break;
            }
            // case mainActionTypes.MAIN_DIAGRAM_DELETE_SUCCESS: {
            //     draft.diagrams = draft.diagrams.filter(diagram => (diagram.id !== action.payload.id));
            //     break;
            // }


            // RESTORE DIAGRAM
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_RESTORE: {
                const diagramId = action.payload.id || null;
                let diagram = null;

                if (diagramId) {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;
                }

                draft.isModalDiagramRestoreEnabled = !state.isModalDiagramRestoreEnabled;
                draft.modalDiagramRestoreData = { id: diagramId, diagram };
                break;
            }

            // CLONE TEMPLATE
            case mainActionTypes.TOGGLE_MODAL_TEMPLATE_CLONE: {
                const templateId = action.payload.id || null;
                let template = null;

                if (templateId) {
                    template = state.templates?.find(template => (template.id === templateId)) || null;
                }

                draft.isModalTemplateCloneEnabled = !state.isModalTemplateCloneEnabled;
                draft.modalTemplateCloneData = { id: templateId, template };
                break;
            }

            // DIAGRAM CREATE
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_CREATE: {
                const page = action.payload.page; // 'main', 'canvas'
                draft.isModalDiagramCreateEnabled = !state.isModalDiagramCreateEnabled;
                draft.modalDiagramCreateData = { page };
                break;
            }
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_CREATE_NEW: {
                const page = action.payload.page; // 'main', 'canvas'
                draft.isModalDiagramCreateNewEnabled = !state.isModalDiagramCreateNewEnabled;
                draft.modalDiagramCreateNewData = { page };
                break;
            }

            // DIAGRAM SHARE
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_SHARE: {
                const page = action.payload.page; // 'main', 'canvas'
                const diagramId = action.payload.id || null;
                let diagram = null;

                draft.isModalDiagramShareEnabled = !state.isModalDiagramShareEnabled;
                draft.modalDiagramShareData = { id: null, diagram: null, page };

                if (!diagramId) {
                    break;
                }

                // OPEN MODAL IN MAIN PAGE
                if (page === 'main') {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;

                    // OPEN MODAL IN CANVAS PAGE
                } else if (page === 'canvas') {
                    if (state.diagramDetailsCanvas?.id !== diagramId)
                        break;
                    diagram = state.diagramDetailsCanvas;
                }

                draft.modalDiagramShareData = { id: diagramId, diagram, page };
                break;
            }

            // DIAGRAM IMPORT SQL
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_IMPORT_SQL: {
                const type = action.payload.type;
                draft.isModalDiagramImportSqlEnabled = !state.isModalDiagramImportSqlEnabled;
                draft.modalDiagramImportSqlData = { type };
                break;
            }
            // DIAGRAM DETAILS
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_DETAILS: {
                const page = action.payload.page; // 'main', 'canvas'
                const diagramId = action.payload.id || null;
                let diagram = null;

                draft.isModalDiagramDetailsEnabled = !state.isModalDiagramDetailsEnabled;
                draft.modalDiagramDetailsData = { id: null, diagram: null, page };

                if (!diagramId) {
                    break;
                }

                // OPEN MODAL IN MAIN PAGE
                if (page === 'main') {
                    diagram = state.diagrams?.find(diagram => (diagram.id === diagramId)) || null;

                    // OPEN MODAL IN CANVAS PAGE
                } else if (page === 'canvas') {
                    if (state.diagramDetailsCanvas?.id !== diagramId)
                        break;
                    diagram = state.diagramDetailsCanvas;
                }

                draft.modalDiagramDetailsData = { id: diagramId, diagram, page };
                break;
            }
            // TEMPLATE DETAILS
            case mainActionTypes.TOGGLE_MODAL_TEMPLATE_DETAILS: {
                const templateId = action.payload.id || null;
                let template = null;

                if (templateId) {
                    template = state.templates?.find(template => (template.id === templateId)) || null;
                }

                draft.isModalTemplateDetailsEnabled = !state.isModalTemplateDetailsEnabled;
                draft.modalTemplateDetailsData = { id: templateId, template };
                break;
            }
            // SUPPORT
            case mainActionTypes.TOGGLE_MODAL_SUPPORT: {
                const type = action.payload.type;
                const page = action.payload.page;

                draft.isModalSupportEnabled = !state.isModalSupportEnabled;
                draft.modalSupportData = { type, page };
                break;
            }

            case mainActionTypes.SUPPORT_REQ_SUCCESS: {
                draft.feedbackSentStatus = 'success';
                break;
            }
            case mainActionTypes.SUPPORT_REQ_FAILURE: {
                draft.feedbackSentStatus = 'error';
                break;
            }
            case mainActionTypes.MAIN_CLEAR_FEEDBACK_SENT_STATUS: {
                draft.feedbackSentStatus = null;
                break;
            }

            // FEEDBACK THANKS
            case mainActionTypes.TOGGLE_MODAL_FEEDBACK_THANKS: {
                const type = action.payload.type;
                const page = action.payload.page;
                draft.isModalFeedbackThanksEnabled = !state.isModalFeedbackThanksEnabled;
                draft.modalFeedbackThanksData = { type, page };
                break;
            }

            // PRICING PLAN LIMIT
            case mainActionTypes.ENABLE_MODAL_PRICING_PLAN_LIMIT: {
                draft.isModalPricingPlanLimitEnabled = true;
                break;
            }
            case mainActionTypes.DISABLE_MODAL_PRICING_PLAN_LIMIT: {
                draft.isModalPricingPlanLimitEnabled = false;
                break;
            }

            // PRICING PLAN UPGRADE
            case mainActionTypes.TOGGLE_MODAL_PRICING_PLAN_UPGRADE: {
                const page = action.payload.page;
                draft.isModalPricingPlanUpgradeEnabled = !state.isModalPricingPlanUpgradeEnabled;
                draft.modalPricingPlanUpgradeData = { page };
                break;
            }
            // INVITE PEOPLE
            case mainActionTypes.TOGGLE_MODAL_INVITE_PEOPLE: {
                draft.isModalInvitePeopleEnabled = !state.isModalInvitePeopleEnabled;
                break;
            }
            case mainActionTypes.INVITE_PEOPLE_SUCCESS: {
                draft.inviteSentStatus = 'success';
                break;
            }
            case mainActionTypes.INVITE_PEOPLE_FAILURE: {
                draft.inviteSentStatus = 'error';
                break;
            }
            case mainActionTypes.MAIN_CLEAR_INVITE_SENT_STATUS: {
                draft.inviteSentStatus = null;
                break;
            }

            // COPY OF CLICK_ESCAPE (IN CANVAS) || MOVE_TO_CANVAS || UNFOCUS ENABLED CONTROLS
            case mainActionTypes.MAIN_CANVAS_STARTED: {
                draft = unfocusEnabledControls(state, draft);
                break;
            }


            // MAIN FETCH DIAGRAM
            case mainActionTypes.MAIN_FETCH_DIAGRAM_REQUEST: {
                draft.loading = true;
                break;
            }
            case mainActionTypes.MAIN_FETCH_DIAGRAM_SUCCESS: {
                draft.loading = false;
                draft.diagramDetailsCanvas = action.payload.data;
                draft.diagramDbEngineCanvas = action.payload.data?.db_engine;
                draft.error = null;
                break;
            }
            case mainActionTypes.MAIN_FETCH_DIAGRAM_FAILURE: {
                draft.loading = false;
                draft.error = action.payload;

                // handle differents errors, not-found/access-denied
                draft.isDiagramCanvasNotFound = true;
                break;
            }

            // // ------------------------------------------------------------------
            // // LOAD_NEW_PROJECT
            // case mainActionTypes.LOAD_NEW_PROJECT: {
            //     draft.diagramDetails = {
            //         title: 'New Project',
            //         description: '',
            //         db_engine: 'postgresql'
            //     };
            //     draft.diagramDbEngine = 'postgresql';
            //     draft.isModalDiagramCreateNewEnabled = true;
            //     break;
            // }
            // ------------------------------------------------------------------
            // // LOAD_SAMPLE_PROJECT DETAILS
            // case mainActionTypes.LOAD_SAMPLE_PROJECT_DETAILS: {
            //     draft.diagramDetails = {
            //         id: 'sample_1',
            //         title: 'Role & Permission Management',
            //         // description: `This sample is a lightweight system for managing users, roles, and permissions in modern web apps. It uses role-based access control (RBAC) to clearly define what each user is allowed to do.\n\nIt also includes built-in session tracking and user settings, so you can monitor activity and let users personalize their experience with minimal setup.`,
            //         description: `This sample is a lightweight system for managing users, roles, and permissions in modern web apps. It uses role-based access control (RBAC) to clearly define what each user is allowed to do.`,
            //         db_engine: 'postgresql',
            //     };
            //     draft.diagramDbEngine = 'postgresql';
            //     break;
            // }

            // ------------------------------------------------------------------
            // MODAL LANGUAGES
            case mainActionTypes.TOGGLE_MODAL_LANGUAGES: {
                draft.isModalLanguagesEnabled = !state.isModalLanguagesEnabled;
                break;
            }

            // ------------------------------------------------------------------
            // MODAL WORKSPACE
            case mainActionTypes.TOGGLE_MODAL_WORKSPACE_CREATE: {
                draft.isModalWorkspaceCreateEnabled = !state.isModalWorkspaceCreateEnabled;
                break;
            }


            // SIDEBAR WORKSPACE MENU
            case mainActionTypes.TOGGLE_SIDEBAR_WORKSPACE_MENU: {
                draft.isSidebarWorkspaceMenuEnabled = !state.isSidebarWorkspaceMenuEnabled;
                break;
            }

            default:
                break;
        }
    });
};

export default mainReducer;
